import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import {
  Button,
  useMediaQuery,
  Checkbox,
  FormControlLabel,
  FormGroup,
  CircularProgress,
  Snackbar,
  Alert,
  Slide,
  Pagination,
  Grid,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import SoftSelect from "components/SoftSelect";
import AnseraLogo from "assets/images/Anser.png";
import { FAST_API_BASE_URL } from "constants";
import { useDomain } from "context/DomainContext";
import { useAuth } from "context/AuthContext";

const DataSync = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const apiUrl = FAST_API_BASE_URL;

  const [collections, setCollections] = useState([]);
  const [collectionItems, setCollectionItems] = useState([]);
  const [selectedCollection, setSelectedCollection] = useState(null);
  const [selectedItemIds, setSelectedItemIds] = useState([]);
  const [loadingItems, setLoadingItems] = useState(false);
  const [saving, setSaving] = useState(false);
  const [contentType, setContentType] = useState(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const [page, setPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10); 
  const rowsPerPage = itemsPerPage * 2; 

  // const { domainId } = useDomain();
    const { currentDomain } = useAuth();
          const domainId = currentDomain?.domain_token;
  const source = localStorage.getItem("source") || "webflow";

  useEffect(() => {
    if (source === "webflow" && contentType === "collections") {
      const fetchCollections = async () => {
        try {
          const res = await axios.get(`${apiUrl}/api/webflow-collections`, {
            headers: {
              "DOMAIN-TOKEN": domainId,
              "Content-Type": "application/json",
            },
          });
          setCollections(res.data.result);
        } catch (err) {
          console.error("Error fetching collections", err.response?.data || err.message);
        }
      };
      fetchCollections();
    }
  }, [apiUrl, contentType, domainId, source]);

  const handleCollectionChange = useCallback(
    async (value) => {
      setSelectedCollection(value);
      setSelectedItemIds([]);
      setCollectionItems([]);
      setLoadingItems(true);

      try {
        const res = await axios.get(`${apiUrl}/api/collection-items/${value}`, {
          headers: {
            "DOMAIN-TOKEN": domainId,
            "Content-Type": "application/json",
          },
        });

        const { result } = res.data;
        const items = result?.result ?? [];
        const synced_ids = result?.synced_ids ?? [];

        
        setCollectionItems(items);
        setSelectedItemIds(synced_ids);
      } catch (err) {
        console.error("Error fetching collection items", err.response?.data || err.message);
      } finally {
        setLoadingItems(false);
      }
    },
    [apiUrl, domainId]
  );

  const fetchPages = useCallback(async () => {
    setLoadingItems(true);
    try {
      let res;
      if (source === "custom") {
        res = await axios.get(`${apiUrl}/api/custom-pages`, {
          headers: {
            "DOMAIN-TOKEN": domainId,
            "Content-Type": "application/json",
          },
        });
      } else {
        res = await axios.get(`${apiUrl}/api/webflow-pages`, {
          headers: {
            "DOMAIN-TOKEN": domainId,
            "Content-Type": "application/json",
          },
        });
      }

      const pagesData = res.data.result?.result ?? [];
      const synced_ids = res.data.result?.synced_ids ?? [];

      setCollectionItems(pagesData);
      setSelectedItemIds(synced_ids);
      setPage(1);
    } catch (err) {
      console.error("Error fetching pages", err.response?.data || err.message);
    } finally {
      setLoadingItems(false);
    }
  }, [apiUrl, domainId, source]);

  const handleContentTypeChange = (value) => {
    setContentType(value);
    setSelectedCollection(null);
    setSelectedItemIds([]);
    setCollectionItems([]);

    if (value === "page") {
      fetchPages();
    }
  };

  const handleItemsPerPageChange = (value) => {
    setItemsPerPage(value);
    setPage(1);
  };

  useEffect(() => {
    if (source === "custom") {
      setContentType("page");
    }
    fetchPages();
  }, [domainId, source, fetchPages]);

  const handleItemCheck = (id) => {
    setSelectedItemIds((prev) =>
      prev.includes(id) ? prev.filter((itemId) => itemId !== id) : [...prev, id]
    );
  };

  const handleSaveChanges = async () => {
    try {
      if (!domainId) {
        setSnackbarMessage("Domain ID not found.");
        setSnackbarSeverity("error");
        setSnackbarOpen(true);
        return;
      }

      if (source === "custom" && contentType === "page") {
        const res = await axios.get(`${apiUrl}/api/custom-pages`, {
          headers: {
            "DOMAIN-TOKEN": domainId,
            "Content-Type": "application/json",
          },
        });

        const currentSyncedIds = res.data.result?.synced_ids || [];

        const newSelectedIds = selectedItemIds.filter(
          (id) => !currentSyncedIds.includes(id)
        );
        const deselectedIds = currentSyncedIds.filter(
          (id) => !selectedItemIds.includes(id)
        );

        if (deselectedIds.length > 0) {
          const unsyncPayload = {
            type: "page",
            item_ids: deselectedIds.map((id) => ({ id })),
          };

          await axios.delete(`${apiUrl}/api/unsync/content`, {
            data: unsyncPayload,
            headers: {
              "DOMAIN-TOKEN": domainId,
              "Content-Type": "application/json",
            },
          });

          setSnackbarMessage("Pages unsynced successfully!");
          setSnackbarSeverity("success");
          setSnackbarOpen(true);
        }

        if (newSelectedIds.length > 0) {
          const syncPayload = collectionItems
            .filter((item) => newSelectedIds.includes(item.id))
            .map((item) => ({
              id: item.id,
              title: item.title,
              url: item.url,
            }));

          await axios.post(`${apiUrl}/api/sync-custom-pages`, syncPayload, {
            headers: {
              "DOMAIN-TOKEN": domainId,
              "Content-Type": "application/json",
            },
          });

          setSnackbarMessage("Custom pages synced successfully!");
           window.dispatchEvent(new CustomEvent('syncStarted'));
          setSnackbarSeverity("success");
          setSnackbarOpen(true);
        }

        if (deselectedIds.length === 0 && newSelectedIds.length === 0) {
          setSnackbarMessage("No changes to save.");
          setSnackbarSeverity("info");
          setSnackbarOpen(true);
        }

        return;
      }

    let apiEndpoint;
    let syncEndpoint;
    
    if (contentType === "collections" && selectedCollection) {
      apiEndpoint = `${apiUrl}/api/collection-items/${selectedCollection}`;
      syncEndpoint = `${apiUrl}/api/sync/content`;
    } else {
      apiEndpoint = `${apiUrl}/api/webflow-pages`;
      syncEndpoint = `${apiUrl}/api/sync/content`;
    }
    const res = await axios.get(apiEndpoint, {
        headers: {
          "DOMAIN-TOKEN": domainId,
          "Content-Type": "application/json",
        },
        params: contentType === "collections" ? {} : { type: contentType }
      });

      const currentSyncedIds = res.data.result?.synced_ids || res.data.synced_ids || [];

      const newSelectedIds = selectedItemIds.filter(
        (id) => !currentSyncedIds.includes(id)
      );
      const deselectedIds = currentSyncedIds.filter(
        (id) => !selectedItemIds.includes(id)
      );

      if (deselectedIds.length > 0) {
        const unsyncPayload = {
          type: contentType,
          item_ids: deselectedIds.map((id) => ({ id })),
        };

        if (contentType === "collections" && selectedCollection) {
          unsyncPayload.collection_id = selectedCollection;
        }

        await axios.delete(`${apiUrl}/api/unsync/content`, {
          data: unsyncPayload,
          headers: {
            "DOMAIN-TOKEN": domainId,
            "Content-Type": "application/json",
          },
        });

        setSnackbarMessage("Items unsynced successfully!");
        setSnackbarSeverity("success");
        setSnackbarOpen(true);
      }

      if (newSelectedIds.length > 0) {
      let syncPayload;
      
      if (contentType === "collections" && selectedCollection) {
        const collectionItemsToSync = collectionItems
          .filter((item) => newSelectedIds.includes(item.id))
          .map((item) => ({
            id: item.id,
            title: item.title,
          }));
        
        syncPayload = {
          type: contentType,
          collection_id: selectedCollection,
          item_ids: collectionItemsToSync,
        };
      } else {
        syncPayload = {
          type: contentType,
          item_ids: newSelectedIds.map((id) => ({ id })),
        };
      }

        await axios.post(syncEndpoint, syncPayload, {
          headers: {
            "DOMAIN-TOKEN": domainId,
            "Content-Type": "application/json",
          },
        });

        setSnackbarMessage("Items synced successfully!");
         window.dispatchEvent(new CustomEvent('syncStarted'));
        setSnackbarSeverity("success");
        setSnackbarOpen(true);
      }
      if (deselectedIds.length === 0 && newSelectedIds.length === 0) {
        setSnackbarMessage("No changes to save.");
        setSnackbarSeverity("info");
        setSnackbarOpen(true);
      }
    } catch (error) {
      console.error("Error saving changes:", error);
      setSnackbarMessage("Failed to save changes.");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    }
  };

  const chunkArray = (arr, chunkSize) => {
    const chunks = [];
    for (let i = 0; i < arr.length; i += chunkSize) {
      chunks.push(arr.slice(i, i + chunkSize));
    }
    return chunks;
  };

  const startIdx = (page - 1) * rowsPerPage;
  const endIdx = startIdx + rowsPerPage;
  const paginatedItems = collectionItems.slice(startIdx, endIdx);
  const leftColumnItems = paginatedItems.slice(0, itemsPerPage);
  const rightColumnItems = paginatedItems.slice(itemsPerPage);

  return (
    <SoftBox
      sx={{
        backgroundColor: "#fff",
        p: isMobile ? 2 : 4,
        borderRadius: "16px",
        boxShadow: "0px 6px 20px rgba(0, 0, 0, 0.06)",
      }}
      display="flex"
      flexDirection="column"
      gap={3}
    >
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={4000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        TransitionComponent={(props) => <Slide {...props} direction="top" />}
      >
        <Alert
          onClose={() => setSnackbarOpen(false)}
          severity={snackbarSeverity}
          variant="filled"
          sx={{
            width: "100%",
            color: "#ffffff",
            backgroundColor:
              snackbarSeverity === "success"
                ? "#008080"
                : snackbarSeverity === "error"
                ? "#d32f2f"
                : undefined,
          }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>

      <SoftBox display="flex" alignItems="center" gap={1}>
        <img src={AnseraLogo} alt="Ansera Logo" width={64} />
        <SoftTypography variant="h6" fontWeight="bold">
          Data Syncing for Better Search Results
        </SoftTypography>
      </SoftBox>

      <SoftTypography
        variant="body2"
        color="text.secondary"
        sx={{ borderBottom: "1px solid #e0e0e0", pb: 1 }}
      >
        Please sync your data using the criteria below to enhance accuracy and performance.
      </SoftTypography>

      <SoftBox display="flex" flexDirection={isMobile ? "column" : "row"} gap={2}>
        <SoftBox flex={1}>
          <SoftTypography variant="button" fontWeight="bold" mb={0.5}>
            Choose Content Type
          </SoftTypography>
          <SoftSelect
            options={[
              { value: "page", label: "Pages" },
              ...(source === "webflow" ? [{ value: "collections", label: "Collections" }] : []),
            ]}
            placeholder="- Select Type -"
            value={
              contentType
                ? { value: contentType, label: contentType.charAt(0).toUpperCase() + contentType.slice(1) }
                : null
            }
            onChange={({ value }) => handleContentTypeChange(value)}
          />
        </SoftBox>

        {source === "webflow" && contentType === "collections" && (
          <SoftBox flex={1}>
            <SoftTypography variant="button" fontWeight="bold" mb={0.5}>
              Filter by Category
            </SoftTypography>
            <SoftSelect
              options={collections.map((cat) => ({
                value: cat.id,
                label: cat.displayName,
              }))}
              placeholder="- Select Collection -"
              onChange={({ value }) => handleCollectionChange(value)}
            />
          </SoftBox>
        )}
      </SoftBox>

      {(contentType === "page" || selectedCollection) && (
        <SoftBox>
          <SoftBox display="flex" alignItems="center" justifyContent="space-between" mb={1}>
            <SoftTypography variant="button" fontWeight="bold">
              Select {contentType === "page" ? "Pages" : "Collection Items"}:
            </SoftTypography>
            <SoftBox sx={{ minWidth: 120 }}>
              <SoftTypography variant="caption" fontWeight="bold" mb={0.5} display="block">
                Items per page
              </SoftTypography>
              <SoftSelect
                options={[
                  { value: 5, label: "5" },
                  { value: 10, label: "10" },
                  { value: 20, label: "20" },
                  { value: 30, label: "30" },
                  { value: 50, label: "50" },
                  { value: 100, label: "100" },
                  { value: 150, label: "150" },
                ]}
                value={{ value: itemsPerPage, label: itemsPerPage.toString() }}
                onChange={({ value }) => handleItemsPerPageChange(value)}
              />
            </SoftBox>
          </SoftBox>
          
          {loadingItems ? (
            <SoftBox display="flex" alignItems="center" justifyContent="center" py={3}>
              <CircularProgress size={28} color="teal" />
            </SoftBox>
          ) : (
            <>
              <Grid container spacing={isMobile ? 2 : 3}>
                <Grid item xs={12} md={6}>
                  <FormGroup>
                    {leftColumnItems.map((item) => (
                      <FormControlLabel
                        key={item.id}
                        control={
                          <Checkbox
                            checked={selectedItemIds.includes(item.id)}
                            onChange={() => handleItemCheck(item.id)}
                          />
                        }
                        label={
                          source === "custom" ? (
                            <SoftBox display="flex" flexDirection="column">
                              <span>{item.title}</span>
                              <SoftTypography variant="caption" color="text.secondary">
                                {item.url}
                              </SoftTypography>
                            </SoftBox>
                          ) : (
                            item.title
                          )
                        }
                      />
                    ))}
                  </FormGroup>
                </Grid>
                
                {rightColumnItems.length > 0 && (
                  <Grid item xs={12} md={6}>
                    <FormGroup>
                      {rightColumnItems.map((item) => (
                        <FormControlLabel
                          key={item.id}
                          control={
                            <Checkbox
                              checked={selectedItemIds.includes(item.id)}
                              onChange={() => handleItemCheck(item.id)}
                            />
                          }
                          label={
                            source === "custom" ? (
                              <SoftBox display="flex" flexDirection="column">
                                <span>{item.title}</span>
                                <SoftTypography variant="caption" color="text.secondary">
                                  {item.url}
                                </SoftTypography>
                              </SoftBox>
                            ) : (
                              item.title
                            )
                          }
                        />
                      ))}
                    </FormGroup>
                  </Grid>
                )}
              </Grid>

              {collectionItems.length > rowsPerPage && (
                <SoftBox display="flex" justifyContent="center" mt={2}>
                  <Pagination
                    count={Math.ceil(collectionItems.length / rowsPerPage)}
                    page={page}
                    onChange={(_, value) => setPage(value)}
                    color="teal"
                  />
                </SoftBox>
              )}
            </>
          )}
        </SoftBox>
      )}

      {saving && (
        <SoftBox>
          <SoftTypography variant="caption" color="text.secondary" sx={{ lineHeight: 2.0 }}>
            Saving changes may take a few minutes depending on the number of items. Please be patient
            and do not refresh the page during the process.
          </SoftTypography>
        </SoftBox>
      )}

      {(contentType === "page" || selectedCollection) && (
        <SoftBox
          display="flex"
          justifyContent={isMobile ? "center" : "flex-start"}
          flexDirection={isMobile ? "column" : "row"}
          gap={2}
          mt={2}
        >
          <Button
            variant="contained"
            size="medium"
            onClick={handleSaveChanges}
            disabled={saving}
            sx={{
              backgroundColor: "#219297",
              color: "#ffffff",
              fontWeight: "bold",
              fontSize: "0.8rem",
              textTransform: "none",
              borderRadius: "8px",
              px: 3,
              py: 1,
              "&:hover": {
                backgroundColor: "#1a767a",
              },
            }}
          >
            {saving ? "Saving..." : "Save Changes"}
          </Button>

          <Button
            variant="contained"
            size="medium"
            onClick={handleSaveChanges}
            sx={{
              backgroundColor: "#219297",
              color: "#fff",
              fontWeight: "bold",
              fontSize: "0.8rem",
              textTransform: "none",
              borderRadius: "8px",
              px: 3,
              py: 1,
              "&:hover": {
                backgroundColor: "#1a767a",
              },
            }}
          >
            Resync
          </Button>
        </SoftBox>
      )}
    </SoftBox>
  );
};

export default DataSync;